import { getAuth } from 'firebase/auth';
import loadUser from '../auth/loadUser';
import config, { firebaseApp } from '../config';

const { apiBase, streamServerBase } = config;

export const jsonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

// This generates the base headers for authentication
// It does not include JSON headers, since useAuthFetch needs to support non-JSON requests
export const getBaseHeaders = async (): Promise<Record<string, string>> => {
  let token;
  try {
    await loadUser();
    token = await getAuth(firebaseApp).currentUser?.getIdToken();
  } catch (error) {
    console.error('Failed to get token', error);
  }

  if (token) return { Authorization: `Bearer ${token}` };
  return {};
};

const SERVICE_UNAVAILABLE_MESSAGE =
  'A server error occurred, and our team has been notified. Please try again later.';

// --- ETag cache for conditional GET requests ---

const ETAG_CACHE_MAX_SIZE = 500;

type EtagCacheEntry = {
  etag: string;
  data: any;
  accessedAt: number;
};

const etagCache = new Map<string, EtagCacheEntry>();

function evictOldestCacheEntry(): void {
  let oldestKey: string | undefined;
  let oldestTime = Infinity;
  etagCache.forEach((entry, key) => {
    if (entry.accessedAt < oldestTime) {
      oldestTime = entry.accessedAt;
      oldestKey = key;
    }
  });
  if (oldestKey) etagCache.delete(oldestKey);
}

/**
 * Invalidate ETag cache entries. When called without arguments, clears the
 * entire cache. When called with a URL pattern, removes all entries whose
 * URL contains the pattern (e.g. "/files/").
 */
export function invalidateEtagCache(urlPattern?: string): void {
  if (!urlPattern) {
    etagCache.clear();
    return;
  }
  const keysToDelete: string[] = [];
  etagCache.forEach((_entry, key) => {
    if (key.includes(urlPattern)) keysToDelete.push(key);
  });
  keysToDelete.forEach((key) => etagCache.delete(key));
}

// Retry 503 and network errors up to 2 times with exponential backoff and jitter
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries: number = 2,
): Promise<Response> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);
      if (response.status === 503 && attempt < maxRetries) {
        const delay = Math.min(1000 * 2 ** attempt, 10000) * (0.5 + Math.random() * 0.5);
        await new Promise((resolve) => setTimeout(resolve, Math.round(delay)));
        continue;
      }
      return response;
    } catch (err) {
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * 2 ** attempt, 10000) * (0.5 + Math.random() * 0.5);
        await new Promise((resolve) => setTimeout(resolve, Math.round(delay)));
      } else {
        throw err;
      }
    }
  }
  throw new Error('Request failed after retries');
}

// This wraps fetch with the Firebase auth token and JSON handling
const authFetchFactory = (fetchBase: string) =>
  async function <B extends boolean>(
    url: string,
    { headers: customHeaders, ...options }: RequestInit = {},
    isJson?: B,
  ): Promise<B extends true ? any : Response> {
    const isGet = !options.method || options.method.toUpperCase() === 'GET';
    const fullUrl = `${fetchBase}${url}`;

    const etagHeaders: Record<string, string> = {};
    if (isGet && isJson !== false) {
      const cached = etagCache.get(fullUrl);
      if (cached) {
        etagHeaders['If-None-Match'] = cached.etag;
      }
    }

    const headers = {
      ...(await getBaseHeaders()),
      ...(isJson !== false ? jsonHeaders : {}),
      ...etagHeaders,
      // Spread user-defined headers *after* to allow overrides
      ...customHeaders,
    };

    const fetchOptions = { ...options, headers };
    let response = await fetchWithRetry(fullUrl, fetchOptions);

    // Handle 304 Not Modified: return previously cached data
    if (response.status === 304 && isGet) {
      const cached = etagCache.get(fullUrl);
      if (cached) {
        cached.accessedAt = Date.now();
        return cached.data;
      }
      // Cache was cleared by a concurrent mutation while this request was
      // in-flight. Retry without If-None-Match to get a full response.
      const retryHeaders = Object.fromEntries(
        Object.entries(headers).filter(([key]) => key !== 'If-None-Match'),
      );
      response = await fetchWithRetry(fullUrl, { ...fetchOptions, headers: retryHeaders });
    }

    // Do not attempt to parse JSON if 503 is returned: https://github.com/GriffinScribe-LLC/recap-web/issues/63
    if (response.status === 503) {
      throw new Error(SERVICE_UNAVAILABLE_MESSAGE);
    }
    if (isJson === false) {
      if (!response.ok) {
        throw response;
      }
      return response;
    }

    // Handle 204 No Content responses which have no body to parse
    if (response.status === 204) {
      return null as any;
    }

    // Guard against empty or non-JSON response bodies
    const text = await response.text();
    let json: any;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      throw new Error(`Invalid JSON response from ${url}`);
    }
    if (!response.ok) {
      throw json || response.status;
    }

    // Successful mutations invalidate the ETag cache so subsequent GETs
    // fetch fresh data instead of receiving a stale 304.
    if (!isGet) {
      etagCache.clear();
    }

    // Cache ETag from successful JSON GET responses
    if (isGet) {
      const responseEtag = response.headers.get('ETag');
      if (responseEtag) {
        if (etagCache.size >= ETAG_CACHE_MAX_SIZE) {
          evictOldestCacheEntry();
        }
        etagCache.set(fullUrl, {
          etag: responseEtag,
          data: json,
          accessedAt: Date.now(),
        });
      }
    }

    return json;
  };

// For cloud functions (almost all requests)
export const authFetch = authFetchFactory(apiBase);

// For livestream captioning server
export const captionServerAuthFetch = authFetchFactory(streamServerBase);
